import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { CreditCard, Plus, Trash2, CheckCircle, AlertCircle } from 'lucide-react'
import api from '../utils/api'
import Card from './ui/Card'
import Button from './ui/Button'

// Mock Stripe components for now - in production you'd use @stripe/react-stripe-js
const CardElement = ({ onChange, options = {} }) => {
  const { t } = useTranslation()
  const [cardComplete, setCardComplete] = useState(true) // Auto-complete for test card
  const [cardError, setCardError] = useState(null)
  
  // Auto-trigger onChange with complete status for test environment
  React.useEffect(() => {
    onChange({
      complete: true,
      error: null
    })
  }, [onChange])

  return (
    <div className="p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {t('payment.testCardInfo')}
      </div>
      <div className="mt-2 space-y-2">
        <input
          type="text"
          placeholder="4242 4242 4242 4242 (Test Visa)"
          defaultValue="4242424242424242"
          className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded text-sm"
          onChange={(e) => {
            const isComplete = e.target.value.length >= 16
            setCardComplete(isComplete)
            onChange({
              complete: isComplete,
              error: isComplete ? null : { message: 'Card number is incomplete' }
            })
          }}
        />
        <div className="flex space-x-2">
          <input
            type="text"
            placeholder="12/25"
            defaultValue="1225"
            className="flex-1 p-2 border border-gray-200 dark:border-gray-600 rounded text-sm"
          />
          <input
            type="text"
            placeholder="123"
            defaultValue="123"
            className="flex-1 p-2 border border-gray-200 dark:border-gray-600 rounded text-sm"
          />
        </div>
      </div>
      <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
        💳 {t('payment.testPaymentNote')}
      </div>
    </div>
  )
}

const PaymentMethodManager = () => {
  const { t } = useTranslation()
  const [showAddCard, setShowAddCard] = useState(false)
  const [cardComplete, setCardComplete] = useState(false)
  const [cardError, setCardError] = useState(null)
  const queryClient = useQueryClient()

  // Fetch payment methods
  const { data: paymentMethods = [], isLoading } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      const response = await api.get('/payments/payment-methods')
      return response.data
    },
  })

  // Add payment method mutation
  const addPaymentMethodMutation = useMutation({
    mutationFn: async (paymentMethodData) => {
      const response = await api.post('/payments/payment-methods', paymentMethodData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      setShowAddCard(false)
      setCardComplete(false)
      setCardError(null)
    },
  })

  // Remove payment method mutation
  const removePaymentMethodMutation = useMutation({
    mutationFn: async (paymentMethodId) => {
      await api.delete(`/payments/payment-methods/${paymentMethodId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    },
  })

  const handleAddPaymentMethod = async () => {
    if (!cardComplete) return

    try {
      // For testing purposes, use Stripe test payment method tokens
      // In production, you would use Stripe Elements to create real payment methods
      const testPaymentMethods = [
        'pm_card_visa',           // Test Visa card
        'pm_card_mastercard',     // Test Mastercard  
        'pm_card_amex',           // Test American Express
        'pm_card_discover',       // Test Discover
      ]
      
      // Use a different test payment method each time (rotate through them)
      const methodIndex = paymentMethods.length % testPaymentMethods.length
      const testPaymentMethodId = testPaymentMethods[methodIndex]
      
      const testPaymentMethod = {
        payment_method_id: testPaymentMethodId,
        set_as_default: paymentMethods.length === 0
      }

      await addPaymentMethodMutation.mutateAsync(testPaymentMethod)
    } catch (error) {
      console.error('Failed to add payment method:', error)
    }
  }

  const handleRemovePaymentMethod = (paymentMethodId) => {
    if (confirm(t('payment.confirmRemoveCard'))) {
      removePaymentMethodMutation.mutate(paymentMethodId)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{t('payment.paymentMethods')}</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              </div>
            ))}
          </div>
        </Card.Content>
      </Card>
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex justify-between items-center">
          <Card.Title>{t('payment.paymentMethods')}</Card.Title>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddCard(!showAddCard)}
            className="flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('payment.addCard')}
          </Button>
        </div>
      </Card.Header>
      <Card.Content>
        {/* Existing Payment Methods */}
        {paymentMethods.length > 0 ? (
          <div className="space-y-4 mb-6">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-white dark:bg-gray-700 rounded border">
                    <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {method.card_brand?.toUpperCase()} •••• {method.card_last4}
                      </p>
                      {method.is_default && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t('payment.default')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('payment.expiresOn', { 
                        month: method.card_exp_month?.toString().padStart(2, '0'), 
                        year: method.card_exp_year 
                      })}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemovePaymentMethod(method.id)}
                  disabled={removePaymentMethodMutation.isPending}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 mb-6">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('payment.noPaymentMethods')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              {t('payment.addCardToUpgrade')}
            </p>
          </div>
        )}

        {/* Add Payment Method Form */}
        {showAddCard && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('payment.addNewCard')}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('payment.cardInformation')}
                </label>
                <CardElement
                  onChange={(event) => {
                    setCardComplete(event.complete)
                    setCardError(event.error)
                  }}
                />
                {cardError && (
                  <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {cardError.message}
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={handleAddPaymentMethod}
                  disabled={!cardComplete || addPaymentMethodMutation.isPending}
                  loading={addPaymentMethodMutation.isPending}
                  className="flex-1"
                >
                  {t('payment.addCard')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddCard(false)
                    setCardComplete(false)
                    setCardError(null)
                  }}
                  className="flex-1"
                >
                  {t('button.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  )
}

export { PaymentMethodManager, CardElement }